import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, FileText, Car, Truck, GaugeCircle, Map, Star, Zap, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { FaRupeeSign, FaClock, FaChartLine, FaPercentage, FaWallet, FaBalanceScale, FaCheckCircle, FaExchangeAlt } from "react-icons/fa";
import { BiCar } from "react-icons/bi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const BMWCarLoan = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "What is the minimum interest rate for a BMW car loan?",
      answer: "Interest rates typically start from 8.5% per annum, depending on the profile.",
    },
    {
      question: "Can I get 100% financing on a BMW car?",
      answer: "Generally, banks offer up to 90% financing on the on-road price.",
    },
    {
      question: "Is BMW Financial Services better than a bank loan?",
      answer: "It depends on your eligibility; they offer tailored EMI plans but it’s best to compare both.",
    },
    {
      question: "Are used BMW cars eligible for loans?",
      answer: "Yes, certified pre-owned BMW vehicles can be financed.",
    },
    {
      question: "What credit score is required for a BMW loan?",
      answer: "A score above 750 is generally recommended for quicker approval.",
    },
    {
      question: "Can business owners apply for a BMW car loan?",
      answer: "Yes, with proper documentation showing financial stability.",
    },
    {
      question: "Can I bundle insurance and registration into the loan?",
      answer: "Yes, most lenders allow it.",
    },
    {
      question: "Are NRIs eligible to apply for a BMW car loan?",
      answer: "Yes, provided there is a resident co-applicant in India.",
    },
    {
      question: "How long does loan approval take?",
      answer: "Usually between 2 to 4 working days after submitting all documents.",
    },
    {
      question: "What are the EMI options available?",
      answer: "You can choose from Standard, Step-Up, Balloon, or EMI Holiday plans.",
    },
    {
      question: "Is a co-applicant necessary?",
      answer: "Not mandatory, but can improve your approval chances and loan amount.",
    },
    {
      question: "Can I prepay or foreclose the loan?",
      answer: "Yes, but some banks may charge a small prepayment fee.",
    },
    {
      question: "What is the minimum income requirement?",
      answer: "Generally, ₹1.25 to ₹1.5 lakh per month is expected for luxury car financing.",
    },
    {
      question: "Do government employees get special rates?",
      answer: "Some public sector banks may offer preferential rates.",
    },
    {
      question: "Is hypothecation mandatory?",
      answer: "Yes, until the loan is repaid fully.",
    },
    {
      question: "Are BMW M models covered under regular car loans?",
      answer: "Yes, though loan terms may differ due to the higher value.",
    },
    {
      question: "Can I refinance my existing BMW loan?",
      answer: "Yes, through balance transfer options at better interest rates.",
    },
    {
      question: "Are women borrowers eligible for special schemes?",
      answer: "Some lenders offer lower rates or processing fee discounts for women.",
    },
    {
      question: "Is loan approval quicker through BMW dealerships?",
      answer: "Yes, especially when using BMW Financial Services.",
    },
    {
      question: "Can I apply online for a BMW car loan?",
      answer: "Yes, TheFinMaster offers a simple online process for comparison and application.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows EMI Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Loans' },
  ];

  const benefits = [
    {
      title: "BMW 2 Series Gran Coupe",
      desc: "Compact luxury with bold styling and premium features.",
      icon: <Car className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "BMW 3 Series & 3 Series Gran Limousine",
      desc: "A blend of sportiness, comfort, and everyday usability.",
      icon: <Truck className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "BMW 5 Series",
      desc: "Executive sedan with a refined, performance-focused design.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "BMW X1 & X3",
      desc: "Compact and mid-size SUVs offering versatility and tech-rich cabins.",
      icon: <Map className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
    {
      title: "BMW X5",
      desc: "Full-size SUV designed for luxury family travel.",
      icon: <Star className="w-7 h-7 text-yellow-500" />,
      gradient: "from-yellow-50 to-yellow-100",
    },
    {
      title: "BMW 7 Series",
      desc: "Ultimate flagship sedan with high-end comfort and prestige.",
      icon: <Zap className="w-7 h-7 text-purple-500" />,
      gradient: "from-purple-50 to-purple-100",
    },
    {
      title: "BMW M Performance Lineup",
      desc: "High-performance sports models for driving enthusiasts.",
      icon: <Shield className="w-7 h-7 text-blue-500" />,
      gradient: "from-blue-50 to-blue-100",
    },
  ];

  const highlights = [
    { icon: <FaRupeeSign className="text-3xl text-blue-700 mb-4" />, feature: "Loan Amount", details: "₹7 Lakh to ₹2.5 Crore+" },
    { icon: <FaClock className="text-3xl text-blue-700 mb-4" />, feature: "Loan Tenure", details: "1 to 7 years" },
    { icon: <FaChartLine className="text-3xl text-blue-700 mb-4" />, feature: "Interest Rate Range", details: "8.5% to 12.75% p.a." },
    { icon: <FaPercentage className="text-3xl text-blue-700 mb-4" />, feature: "Processing Fees", details: "0.25% to 1.5% of loan amount" },
    { icon: <FaWallet className="text-3xl text-blue-700 mb-4" />, feature: "Minimum Down Payment", details: "10% to 30% of the on-road price" },
    { icon: <FaBalanceScale className="text-3xl text-blue-700 mb-4" />, feature: "Loan to Value (LTV)", details: "Up to 90% financing possible" },
    { icon: <FaCheckCircle className="text-3xl text-blue-700 mb-4" />, feature: "Approval Timeline", details: "48–96 hours post document submission" },
    { icon: <FaExchangeAlt className="text-3xl text-blue-700 mb-4" />, feature: "EMI Options", details: "Standard, Step-Up, Balloon, EMI Holiday" },
  ];

  return (
    <>
      <Helmet>
        <title>BMW Car Loan in India – Compare Rates & Apply Online</title>
        <meta
          name="description"
          content="Explore BMW car loan options in India. Compare interest rates, EMI, and apply online for models like 3 Series, X1, 5 Series, and more."
        />
        <meta
          name="keywords"
          content="bmw car loan, bmw 3 series loan, bmw x1 loan, car loan emi calculator"
        />
          <link rel="canonical" href="https://thefinmaster.com/bmw-car-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/bmw-car-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="BMW Car Loan in India" />
        <meta property="og:description" content="Compare and apply for BMW car loans online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/bmw-car-loan" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">BMW Car Loan</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">BMW Car Loan </span>  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                BMW is known for its luxury, performance, and cutting-edge technology that deliver a thrilling driving experience. Whether you are planning to buy a sporty 3 Series or a powerful X5, the right car loan can make your purchase smoother and more affordable. This guide walks you through everything you need to know about getting a car loan for a BMW, from interest rates and eligibility to documentation, repayment tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/bmwcarloan.jpeg"
                alt="BMW Car"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            Popular BMW Cars Eligible for Car Loans
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            BMW cars are widely financed by both major Indian banks and luxury loan providers. New and certified pre-owned BMW cars are both eligible for financing with flexible loan structures.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="w-full bg-gradient-to-b from-white via-blue-50 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center mb-4">
             BMW Car Loan Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 md:px-4">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden p-6 rounded-2xl shadow-xl bg-white/70 backdrop-blur-xl border border-gray-200 transition-transform transform hover:scale-105"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-300 via-pink-300 to-yellow-300 rounded-full opacity-30 blur-3xl group-hover:opacity-50 transition-all"></div>
                <div className="text-3xl text-blue-700 mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.feature}</h3>
                <p className="text-gray-700">{item.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Banks Offering BMW Car Loans
        </h2>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { lender: "HDFC Bank", rate: "8.5%", tenure: "7 years", features: "Instant approvals, luxury segment loans" },
              { lender: "Axis Bank", rate: "9.0%", tenure: "5 years", features: "High LTV for luxury vehicles" },
              { lender: "ICICI Bank", rate: "8.75%", tenure: "7 years", features: "Customized offers for salaried/self-employed" },
              { lender: "Kotak Mahindra Bank", rate: "9.25%", tenure: "6 years", features: "Flexible repayment options" },
              { lender: "IndusInd Bank", rate: "9.50%", tenure: "7 years", features: "Focused loan programs for professionals" },
              { lender: "YES BANK", rate: "9.75%", tenure: "6 years", features: "Fast-tracked loan approvals" },
              { lender: "BMW Financial Services", rate: "Custom", tenure: "7 years", features: "In-house financing with tailored EMIs" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.lender}</h3>
                <div className="text-gray-600 mb-1"><strong>Starting Interest Rate:</strong> {item.rate}</div>
                <div className="text-gray-600 mb-1"><strong>Max Tenure:</strong> {item.tenure}</div>
                <div className="text-gray-600"><strong>Key Features:</strong> {item.features}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Lender</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Interest Rate</th>
                  <th className="p-3 border border-gray-300 text-left">Max Tenure</th>
                  <th className="p-3 border border-gray-300 text-left">Key Features</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["HDFC Bank", "8.5%", "7 years", "Instant approvals, luxury segment loans"],
                  ["Axis Bank", "9.0%", "5 years", "High LTV for luxury vehicles"],
                  ["ICICI Bank", "8.75%", "7 years", "Customized offers for salaried/self-employed"],
                  ["Kotak Mahindra Bank", "9.25%", "6 years", "Flexible repayment options"],
                  ["IndusInd Bank", "9.50%", "7 years", "Focused loan programs for professionals"],
                  ["YES BANK", "9.75%", "6 years", "Fast-tracked loan approvals"],
                  ["BMW Financial Services", "Custom", "7 years", "In-house financing with tailored EMIs"],
                ].map(([lender, rate, tenure, features], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{lender}</td>
                    <td className="p-3 border border-gray-300">{rate}</td>
                    <td className="p-3 border border-gray-300">{tenure}</td>
                    <td className="p-3 border border-gray-300">{features}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Loan EMI <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your EMI — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-loan-emi-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Sample EMI Calculation for BMW Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Use TheFinMaster’s EMI Calculator to fine-tune your EMI by adjusting tenure, interest rate, or down payment.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "BMW 2 Series Gran Coupe", price: "₹50 Lakh", loan: "₹45 Lakh", emi: "₹92,400" },
              { model: "BMW 3 Series", price: "₹65 Lakh", loan: "₹58.5 Lakh", emi: "₹1,20,000" },
              { model: "BMW X1", price: "₹55 Lakh", loan: "₹49.5 Lakh", emi: "₹1,01,500" },
              { model: "BMW 5 Series", price: "₹90 Lakh", loan: "₹81 Lakh", emi: "₹1,66,000" },
              { model: "BMW X5", price: "₹1.25 Crore", loan: "₹1.12 Crore", emi: "₹2,30,000" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>On-Road Price:</strong> {item.price}</div>
                <div className="text-gray-600 mb-1"><strong>Loan Amount:</strong> {item.loan}</div>
                <div className="text-gray-600"><strong>EMI (5 Years @10%):</strong> {item.emi}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">On-Road Price (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Loan Amount (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">EMI (5 Years @10%)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["BMW 2 Series Gran Coupe", "₹50 Lakh", "₹45 Lakh", "₹92,400"],
                  ["BMW 3 Series", "₹65 Lakh", "₹58.5 Lakh", "₹1,20,000"],
                  ["BMW X1", "₹55 Lakh", "₹49.5 Lakh", "₹1,01,500"],
                  ["BMW 5 Series", "₹90 Lakh", "₹81 Lakh", "₹1,66,000"],
                  ["BMW X5", "₹1.25 Crore", "₹1.12 Crore", "₹2,30,000"],
                ].map(([model, price, loan, emi], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{price}</td>
                    <td className="p-3 border border-gray-300">{loan}</td>
                    <td className="p-3 border border-gray-300">{emi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for BMW Car Loan
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Salaried Individuals:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300 mb-6">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 3 to 6 months' salary slips</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Form 16 or ITR of the last 2 years</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bank statements for the past 6 months</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Address proof (utility bill, rent agreement, etc.)</span>
              </div>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Self-Employed Professionals:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>GST certificate or business registration documents</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>ITRs for last 2–3 years</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Audited profit and loss statements</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Business address proof</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 6 months' business bank statements</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <BiCar />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning a BMW is not just about mobility — it’s about experiencing innovation, elegance, and superior driving dynamics. Whether you're buying your first luxury car or upgrading to a newer model, TheFinMaster helps you find the best financing options tailored to your lifestyle.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Compare rates, customize your EMI, and apply online with ease. Drive home your BMW with financial clarity and confidence.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default BMWCarLoan;